import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { Login } from './pages/Login';
import Dashboard from './pages/Dashboard';
import Devices from './pages/Devices';
import SleepMonitoring from './pages/SleepMonitoring';
import VitalSigns from './pages/VitalSigns';
import ShiftSettingsPage from './pages/ShiftSettings';
import GroupManagement from './pages/GroupManagement';
import UserManagement from './pages/UserManagement';
import ResetPassword from './pages/ResetPassword';
import EmployeeManagement from './pages/EmployeeManagement';
import EmployeeDetail from './pages/EmployeeDetail';
import EmployeeCreate from './pages/EmployeeCreate';
import EmployeeEdit from './pages/EmployeeEdit';
import EmployeeBulkImport from './pages/EmployeeBulkImport';
import ParameterSettings from './pages/ParameterSettings';

// ProtectedRoute wrapper component for authentication guards
interface ProtectedRouteProps {
  children: ReactNode;
}

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const token = localStorage.getItem('authToken');

  // Redirect to login if no authentication token exists
  if (!token) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Protected routes - Require authentication */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/devices"
          element={
            <ProtectedRoute>
              <Devices />
            </ProtectedRoute>
          }
        />
        <Route
          path="/sleep-monitoring"
          element={
            <ProtectedRoute>
              <SleepMonitoring />
            </ProtectedRoute>
          }
        />
        <Route
          path="/vital-signs"
          element={
            <ProtectedRoute>
              <VitalSigns />
            </ProtectedRoute>
          }
        />
        <Route
          path="/shift-settings"
          element={
            <ProtectedRoute>
              <ShiftSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/group-management"
          element={
            <ProtectedRoute>
              <GroupManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/user-management"
          element={
            <ProtectedRoute>
              <UserManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/employees"
          element={
            <ProtectedRoute>
              <EmployeeManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/employees/new"
          element={
            <ProtectedRoute>
              <EmployeeCreate />
            </ProtectedRoute>
          }
        />
        <Route
          path="/employees/bulk-import"
          element={
            <ProtectedRoute>
              <EmployeeBulkImport />
            </ProtectedRoute>
          }
        />
        <Route
          path="/employees/:id/edit"
          element={
            <ProtectedRoute>
              <EmployeeEdit />
            </ProtectedRoute>
          }
        />
        <Route
          path="/employees/:id"
          element={
            <ProtectedRoute>
              <EmployeeDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/parameter-settings"
          element={
            <ProtectedRoute>
              <ParameterSettings />
            </ProtectedRoute>
          }
        />

        {/* Catch-all route - Redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
